import { useEffect, useState, useMemo, useCallback } from 'react';
import { listProjects } from '../../api/projects';
import type { GrandProjet } from '../../types/estimation';
import { computeN1, FALLBACK_RATIOS, FALLBACK_BENCHMARKS } from '../../utils/estimation';
import { fmt, fmtCHF } from '../../utils/format';
import { toast } from '../../stores/uiStore';
import { useTranslation } from 'react-i18next';
import { InfoTip } from '../shared/InfoTip';
import styles from './ConsultationPage.module.css';

const TOPIC_KEYS = ['housing', 'transport', 'green', 'schools', 'employment', 'infra', 'culture', 'timeline'] as const;

const TOPIC_ICONS = ['🏠', '🚌', '🌳', '🏫', '💼', '🔧', '🎭', '📅'];

type Sentiment = 'support' | 'concern' | 'question';

interface FeedbackEntry {
  topicKey: string;
  sentiment: Sentiment;
  comment: string;
}

export function ConsultationPage() {
  const { t } = useTranslation(['consult', 'common']);
  const [projects, setProjects] = useState<GrandProjet[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState<Record<string, { sentiment: Sentiment | null; comment: string }>>({});
  const [submitted, setSubmitted] = useState(false);
  const [allFeedback, setAllFeedback] = useState<FeedbackEntry[]>([]);

  const topics = useMemo(() =>
    TOPIC_KEYS.map((key, i) => ({
      key,
      name: t(`consult:topic${key.charAt(0).toUpperCase() + key.slice(1)}`),
      description: t(`consult:topic${key.charAt(0).toUpperCase() + key.slice(1)}Desc`),
      icon: TOPIC_ICONS[i],
    })),
    [t]
  );

  useEffect(() => {
    listProjects({ is_custom: true }).then((data) => {
      setProjects(data);
      if (data.length > 0) setSelectedProjectId(data[0]!.id);
      setLoading(false);
    }).catch(() => {
      toast.error(t('common:failedToLoad'));
      setLoading(false);
    });
  }, []);

  const selectedProject = projects.find((p) => p.id === selectedProjectId);

  // Compute project stats
  const stats = useMemo(() => {
    if (!selectedProject) return null;
    const ha = Number(selectedProject.ha_total ?? 0);
    const haZi = Number(selectedProject.ha_industriel ?? 0);
    const result = computeN1(ha, haZi, FALLBACK_RATIOS, FALLBACK_BENCHMARKS);
    return {
      logements: selectedProject.logements || result.logements,
      inhabitants: result.inhabitants,
      emplois: selectedProject.emplois || result.emplois,
      sbp: selectedProject.sbp_total || result.sbp_total,
      espaces_publics: result.espaces_publics,
      classes: result.classes,
      creches: result.creches,
      cost: result.cost_total,
      ha,
      haZi,
    };
  }, [selectedProject]);

  const initFeedback = useCallback(() => {
    const init: Record<string, { sentiment: Sentiment | null; comment: string }> = {};
    for (const k of TOPIC_KEYS) init[k] = { sentiment: null, comment: '' };
    setFeedback(init);
    setSubmitted(false);
  }, []);

  useEffect(() => { initFeedback(); }, [initFeedback, selectedProjectId]);

  function handleSentiment(topicKey: string, sentiment: Sentiment) {
    setFeedback((prev) => ({
      ...prev,
      [topicKey]: {
        ...prev[topicKey]!,
        sentiment: prev[topicKey]?.sentiment === sentiment ? null : sentiment,
      },
    }));
  }

  function handleComment(topicKey: string, comment: string) {
    setFeedback((prev) => ({
      ...prev,
      [topicKey]: { ...prev[topicKey]!, comment },
    }));
  }

  function handleSubmit() {
    const entries: FeedbackEntry[] = [];
    for (const [topicKey, data] of Object.entries(feedback)) {
      if (data.sentiment) {
        entries.push({ topicKey, sentiment: data.sentiment, comment: data.comment });
      }
    }
    if (entries.length === 0) {
      toast.error(t('common:feedbackMinTopic'));
      return;
    }
    // In production this would POST to API — for now store locally
    setAllFeedback((prev) => [...prev, ...entries]);
    setSubmitted(true);
    toast.success(t('common:feedbackSubmitted', { count: entries.length }));
  }

  // Aggregated sentiment per topic
  const aggregated = useMemo(() => {
    const agg: Record<string, { support: number; concern: number; question: number }> = {};
    for (const k of TOPIC_KEYS) agg[k] = { support: 0, concern: 0, question: 0 };
    for (const f of allFeedback) {
      if (agg[f.topicKey]) {
        agg[f.topicKey]![f.sentiment]++;
      }
    }
    return agg;
  }, [allFeedback]);

  if (loading) return <div className={styles.loading}>{t('common:loading')}</div>;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h2>{t('consult:title')}<InfoTip text={t('consult:tooltips.topicFeedback')} /></h2>
          <div className={styles.headerSub}>
            {t('consult:subtitle')}
          </div>
        </div>
      </div>

      {/* Project selector */}
      <div className={styles.projectSelect}>
        <label>{t('consult:projectLabel')}</label>
        <select
          value={selectedProjectId}
          onChange={(e) => { setSelectedProjectId(e.target.value); initFeedback(); }}
        >
          {projects.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
      </div>

      {/* Project preview */}
      {selectedProject && stats && (
        <div className={styles.preview}>
          <div className={styles.previewTitle}>{selectedProject.name}</div>
          <div className={styles.previewCommunes}>
            {selectedProject.communes.join(', ')} — {stats.ha} ha
          </div>
          <div className={styles.previewDescription}>
            {selectedProject.description || t('common:projectDescription', {
              ha: stats.ha,
              ziPart: stats.haZi > 0 ? t('common:plusZiHa', { ha: stats.haZi }) : '',
              logements: fmt(stats.logements),
              inhabitants: fmt(stats.inhabitants),
              emplois: fmt(stats.emplois),
              espaces: fmt(stats.espaces_publics),
              classes: stats.classes,
              creches: stats.creches,
              plural: stats.creches !== 1 ? 's' : '',
              cost: fmtCHF(stats.cost),
            })}
          </div>

          {/* KPI cards */}
          <div className={styles.kpiGrid}>
            <div className={styles.kpiCard}>
              <div className={styles.kpiValue}>{fmt(stats.logements)}</div>
              <div className={styles.kpiLabel}>{t('consult:housingUnits')}</div>
            </div>
            <div className={styles.kpiCard}>
              <div className={styles.kpiValue}>{fmt(stats.inhabitants)}</div>
              <div className={styles.kpiLabel}>{t('consult:newResidents')}</div>
            </div>
            <div className={styles.kpiCard}>
              <div className={styles.kpiValue}>{fmt(stats.emplois)}</div>
              <div className={styles.kpiLabel}>{t('consult:jobsCreated')}</div>
            </div>
            <div className={styles.kpiCard}>
              <div className={styles.kpiValue}>{fmt(stats.espaces_publics)}</div>
              <div className={styles.kpiLabel}>{t('consult:greenSpace')}</div>
            </div>
            <div className={styles.kpiCard}>
              <div className={styles.kpiValue}>{stats.classes}</div>
              <div className={styles.kpiLabel}>{t('consult:classrooms')}</div>
            </div>
            <div className={styles.kpiCard}>
              <div className={styles.kpiValue}>{fmtCHF(stats.cost)}</div>
              <div className={styles.kpiLabel}>{t('consult:investmentLabel')}</div>
            </div>
          </div>
        </div>
      )}

      {submitted && (
        <div className={styles.submittedBanner}>
          {t('consult:thankYou')}
        </div>
      )}

      {/* Feedback form */}
      {!submitted && (
        <>
          <div className={styles.topicsSection}>
            <div className={styles.sectionTitle}>{t('consult:shareYourFeedback')}</div>
            <div className={styles.topicGrid}>
              {topics.map((topic) => {
                const fb = feedback[topic.key];
                return (
                  <div key={topic.key} className={styles.topicCard}>
                    <div className={styles.topicHeader}>
                      <span className={styles.topicIcon}>{topic.icon}</span>
                      <span className={styles.topicName}>{topic.name}</span>
                    </div>
                    <div className={styles.topicDesc}>{topic.description}</div>
                    <div className={styles.sentimentRow}>
                      <button
                        className={`${styles.sentimentBtn} ${styles.sentimentSupport} ${fb?.sentiment === 'support' ? styles.sentimentSupportActive : ''}`}
                        onClick={() => handleSentiment(topic.key, 'support')}
                      >
                        {t('consult:support')}
                      </button>
                      <button
                        className={`${styles.sentimentBtn} ${styles.sentimentConcern} ${fb?.sentiment === 'concern' ? styles.sentimentConcernActive : ''}`}
                        onClick={() => handleSentiment(topic.key, 'concern')}
                      >
                        {t('consult:concern')}
                      </button>
                      <button
                        className={`${styles.sentimentBtn} ${styles.sentimentQuestion} ${fb?.sentiment === 'question' ? styles.sentimentQuestionActive : ''}`}
                        onClick={() => handleSentiment(topic.key, 'question')}
                      >
                        {t('consult:question')}
                      </button>
                    </div>
                    {fb?.sentiment && (
                      <div className={styles.commentSection}>
                        <textarea
                          className={styles.commentInput}
                          placeholder={t('consult:feedbackPlaceholder', { sentiment: t(`consult:${fb.sentiment}`).toLowerCase(), topic: topic.name.toLowerCase() })}
                          value={fb.comment}
                          onChange={(e) => handleComment(topic.key, e.target.value)}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          <div className={styles.actions}>
            <button className={`${styles.btn} ${styles.btnPrimary}`} onClick={handleSubmit}>
              {t('consult:submitFeedback')}
            </button>
            <button className={styles.btn} onClick={initFeedback}>
              {t('consult:resetFeedback')}
            </button>
          </div>
        </>
      )}

      {/* Aggregated results */}
      {allFeedback.length > 0 && (
        <div className={styles.resultsSection}>
          <div className={styles.sectionTitle}>{t('consult:feedbackSummary', { count: allFeedback.length })}</div>
          {topics.map((topic) => {
            const agg = aggregated[topic.key]!;
            const total = agg.support + agg.concern + agg.question;
            if (total === 0) return null;
            return (
              <div key={topic.key} className={styles.resultBar}>
                <div className={styles.resultTopic}>
                  {topic.icon} {topic.name}
                </div>
                <div className={styles.resultBarWrap}>
                  {agg.support > 0 && (
                    <div
                      className={styles.resultBarSegment}
                      style={{ width: `${(agg.support / total) * 100}%`, background: '#6366f1' }}
                    />
                  )}
                  {agg.concern > 0 && (
                    <div
                      className={styles.resultBarSegment}
                      style={{ width: `${(agg.concern / total) * 100}%`, background: '#f39c12' }}
                    />
                  )}
                  {agg.question > 0 && (
                    <div
                      className={styles.resultBarSegment}
                      style={{ width: `${(agg.question / total) * 100}%`, background: '#22d3ee' }}
                    />
                  )}
                </div>
                <div className={styles.resultCounts}>
                  <span style={{ color: '#6366f1' }}>{agg.support}</span>
                  <span style={{ color: '#f39c12' }}>{agg.concern}</span>
                  <span style={{ color: '#22d3ee' }}>{agg.question}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
